import React, { ReactNode } from 'react';

import { List } from 'antd';
import styled from 'styled-components';

import { canAccessSalesBattlecardContent } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import {
  CMI_DIGEST,
  COMMUNITY_REVIEW_ALERTS,
  COMPETITOR_INCENTIVE_UPDATES,
  MARKET_INTELLIGENCE_DIGEST,
  MARKET_RATE_COMPARISON_UPDATES,
  ONLINE_REVIEWS_DIGEST,
  SALES_BATTLECARD_DIGEST,
} from '../constants';
import SettingsListItem from '../SettingsListItem';

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: var(--text-primary);
  padding-left: 10px;
`;

const Content = styled.div`
  padding: 30px;
`;

const StyledList = styled(List)`
  margin-top: 20px;

  && .ant-list-item:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  && .ant-list-item:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .custom-list-item-meta .ant-list-item-meta-avatar {
    margin-inline-end: 0 !important;
  }

  .custom-list-item-meta .ant-list-item-meta-description {
    font-size: 12px !important;
    line-height: 12px !important;
  }

  .custom-list-item-meta .ant-list-item-meta-title {
    margin-bottom: 0 !important;
  }
`;

const EmailNotifications = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const canViewAllSettings = Boolean(currentUser?.isSuperuser || currentUser?.feature_flags?.view_pricing_data);
  const canViewSalesBattlecard = canAccessSalesBattlecardContent(currentUser);
  const isFurtherAdmin = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);
  const showCmiDigest = Boolean(currentUser?.feature_flags?.show_cmi_digest) || isFurtherAdmin;

  const cmiDigest = isFurtherAdmin
    ? {
        ...CMI_DIGEST,
        title: 'CMI Digest',
        description:
          'Choose if an AI based digest email is sent summarizing the most impactful things happening across communities each week.',
      }
    : CMI_DIGEST;

  const notificationItems = (() => {
    if (!canViewAllSettings) {
      return [ONLINE_REVIEWS_DIGEST, COMMUNITY_REVIEW_ALERTS];
    }

    if (canViewSalesBattlecard) {
      return [
        MARKET_INTELLIGENCE_DIGEST,
        SALES_BATTLECARD_DIGEST,
        ONLINE_REVIEWS_DIGEST,
        MARKET_RATE_COMPARISON_UPDATES,
        COMPETITOR_INCENTIVE_UPDATES,
        COMMUNITY_REVIEW_ALERTS,
      ];
    }

    return [
      MARKET_INTELLIGENCE_DIGEST,
      ONLINE_REVIEWS_DIGEST,
      MARKET_RATE_COMPARISON_UPDATES,
      COMPETITOR_INCENTIVE_UPDATES,
      COMMUNITY_REVIEW_ALERTS,
    ];
  })();

  const items = showCmiDigest ? [cmiDigest, ...notificationItems] : notificationItems;

  return (
    <Content>
      <Title>Email Notifications</Title>
      <StyledList
        bordered
        dataSource={items}
        renderItem={(item: any): ReactNode => <SettingsListItem item={item} isBeta={item === cmiDigest} />}
      />
    </Content>
  );
};

export default EmailNotifications;
